import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import DialpadRoundedIcon from '@mui/icons-material/DialpadRounded';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

import { useDialNumber } from 'components/CallScribe/Home/useDialNumber';

import { useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import {
  CONTACT_NO_PHONE,
  CONTACT_PICKER_SUFFIX,
  TICKET_PICKER_SUFFIX,
  isContactScreen,
} from 'components/CallScribe/contactScreen';
import {
  CallScribeCallDirection,
  ICallScribeCallListItem,
} from 'services/callScribe/types';
import { IPodData } from 'services/pod/types';
import { CallScribeAccessSelector } from 'store/callScribe/selectors';

import AddPersonOverlay, { IDialPick } from 'components/CallScribe/Conference/AddPersonOverlay';
import PeopleList, { flattenGroups, personOptionId, stepActive } from 'components/CallScribe/Conference/PeopleList';
import { displayPhone, personCallName, personShortTitle } from 'components/CallScribe/Conference/PersonRow';
import { IPerson, usePeopleDirectory } from 'components/CallScribe/Conference/usePeopleDirectory';
import CountryDialField, { numberHelper } from 'components/CallScribe/Home/CountryDialField';
import { IDialContactLike } from 'components/CallScribe/Home/dialContactOptions';
import useHomeStyles from 'components/CallScribe/Home/homeStyles';
import { IUseTicketPodDataResult } from 'components/CallScribe/Home/useTicketPodData';
import { TPlaceOutbound } from 'components/CallScribe/Home/usePlaceOutbound';
import { type IPopupBusy, popupBusyMessage } from 'components/CallScribe/startPopupCall';
import { isEmbeddedInIframe } from 'components/CallScribe/popout';
import { IUseTwilioDeviceResult } from 'components/CallScribe/useTwilioDevice';
import { TFieldHelperTone } from 'components/CallScribe/ui/Field';

const NO_POD_DATA: Partial<IPodData> = {};
const NO_RELOAD = (): void => undefined;

export interface IDialBarProps {
  calls: ICallScribeCallListItem[];
  twilio: IUseTwilioDeviceResult;
  ticket: IUseTicketPodDataResult | null;
  placeOutbound: TPlaceOutbound;
  dialFailed: boolean;
  busyWith?: IPopupBusy | null;
  hideLabel?: boolean;
}

const DialBar: React.FC<IDialBarProps> = ({
  calls,
  twilio,
  ticket,
  placeOutbound,
  dialFailed,
  busyWith = null,
  hideLabel = false,
}) => {
  const classes = useHomeStyles();
  const config = useAppSelector(ConfigurationSelector);
  const contactScreen = isContactScreen(config);
  const access = useAppSelector(CallScribeAccessSelector);
  const podData = ticket?.podData ?? NO_POD_DATA;
  const crmId = ticket ? ticket.crmId : config.psaId ?? null;
  const settled = ticket?.settled ?? true;
  const reload = ticket?.reload ?? NO_RELOAD;

  const [selected, setSelected] = useState<IPerson | null>(null);
  const [nameQuery, setNameQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const preselectedRef = useRef(false);
  const countryTouchedRef = useRef(false);
  const listId = useId();
  const fieldId = useId();

  const defaultCountry = useMemo<CountryCode>(() => {
    const declared = (access?.defaultCountryCode ?? '').trim().toUpperCase();
    if (declared.length === 2) {
      return declared as CountryCode;
    }
    const candidates: Array<string | null | undefined> = [
      access?.defaultFromNumber,
      ...calls
        .filter(c => c.direction === CallScribeCallDirection.Outbound)
        .map(c => c.fromNumber),
    ];
    for (const raw of candidates) {
      if (!raw) continue;
      const parsed = parsePhoneNumberFromString(raw);
      if (parsed?.country) return parsed.country;
    }
    return 'US';
  }, [access?.defaultCountryCode, access?.defaultFromNumber, calls]);

  const markCountryTouched = useCallback(() => { countryTouchedRef.current = true; }, []);
  const {
    country,
    national,
    applyRaw: applyRawNumber,
    chooseCountry,
    setDefaultCountry,
  } = useDialNumber(defaultCountry, markCountryTouched);

  useEffect(() => {
    if (countryTouchedRef.current) return;
    setDefaultCountry(defaultCountry);
  }, [defaultCountry, setDefaultCountry]);

  const ticketOptions = useMemo<IDialContactLike[]>(() => {
    const name = (podData.ContactName ?? '').trim();
    if (!name) return [];
    const contactId = podData.ContactId != null && String(podData.ContactId).length > 0
      ? String(podData.ContactId)
      : null;
    const primary = (podData.ContactPhone ?? '').trim();
    const opts: IDialContactLike[] = [
      {
        key: 'ticket-primary',
        contactId,
        name: `${name} (${contactScreen ? CONTACT_PICKER_SUFFIX : TICKET_PICKER_SUFFIX})`,
        phone: primary,
        ticket: true,
      },
    ];
    const seen = new Set([primary.replace(/\D+/g, '')]);
    (podData.communicationItems ?? [])
      .filter(i => i.Type === 'phone' && !!i.Value)
      .forEach(i => {
        const digits = i.Value.replace(/\D+/g, '');
        if (!digits || seen.has(digits)) return;
        seen.add(digits);
        opts.push({
          key: `ticket-${digits}`,
          contactId,
          name: i.Name ? `${name} (${i.Name})` : name,
          phone: i.Value,
          ticket: true,
        });
      });
    return opts;
  }, [podData.ContactName, podData.ContactId, podData.ContactPhone, podData.communicationItems, contactScreen]);

  const ticketPeople = useMemo<IPerson[]>(
    () => ticketOptions
      .filter(o => o.phone.trim().length > 0)
      .map(o => ({
        key: o.key,
        kind: 'ticket',
        source: 'ticket',
        name: o.name,
        fieldName: (podData.ContactName ?? '').trim() || undefined,
        phone: o.phone,
        contactExternalId: o.contactId ?? config.contactId ?? null,
      })),
    [ticketOptions, config.contactId, podData.ContactName],
  );

  const directory = usePeopleDirectory({
    open: true,
    mode: 'dial',
    crmSourceId: crmId,
    suggestCrmSourceId: config.psaId ?? null,
    companyId: podData.CompanyId,
    ticketPeople,
    recentCalls: calls,
    country,
    national,
    nameQuery,
  });
  const people = flattenGroups(directory.groups);
  const query = nameQuery || national;
  const popoverOpen = listOpen && people.length > 0;
  const active = people.find(p => p.key === activeKey) ?? null;

  useEffect(() => {
    if (activeKey && !people.some(p => p.key === activeKey)) setActiveKey(null);
  }, [people, activeKey]);

  useEffect(() => {
    if (preselectedRef.current) return;
    const primary = ticketPeople[0];
    if (primary) {
      preselectedRef.current = true;
      setSelected(primary);
    }
  }, [ticketPeople]);

  const hasTicket = ticket !== null;
  const podDataFailed = hasTicket && settled && podData.CompanyId == null && ticketOptions.length === 0;
  const contactPhoneMissing =
    hasTicket && contactScreen && settled && !podDataFailed && ticketOptions.every(o => !o.phone.trim());
  const podDataFailedCopy = contactScreen
    ? 'This contact could not be loaded from the PSA.'
    : 'This ticket’s PSA connection is not available in MSP Process, so its contacts could not be loaded.';
  const podDataPartialCopy = contactScreen
    ? 'This contact could not be loaded from the PSA, so this list may be missing contacts.'
    : 'This ticket’s PSA connection is not available, so this list may be missing contacts.';

  const contactsFailed = directory.failed.length > 0;
  const pickerLoading = !settled || directory.loading;
  const pickerPhase = (() => {
    if (pickerLoading) return 'loading';
    if (contactsFailed || podDataFailed) return directory.anyoneDialable ? 'partial' : 'error';
    if (directory.anyoneDialable) return 'ready';
    return 'empty';
  })();

  const retryContacts = useCallback(() => {
    if (podDataFailed) reload();
    directory.retry();
  }, [podDataFailed, reload, directory]);

  const manualParsed = useMemo(
    () => parsePhoneNumberFromString(national, country),
    [national, country],
  );
  const manualValid = manualParsed?.isValid() ?? false;

  const selectedParsed = useMemo(() => {
    const raw = (selected?.phone ?? '').trim();
    return raw ? parsePhoneNumberFromString(raw, defaultCountry) : undefined;
  }, [selected, defaultCountry]);
  const selectedValid = selectedParsed?.isValid() ?? false;

  const target = useMemo(() => {
    if (selected) return selectedValid && selectedParsed ? selectedParsed.number : '';
    return manualValid && manualParsed ? manualParsed.number : '';
  }, [selected, selectedValid, selectedParsed, manualValid, manualParsed]);

  const callable = target.length > 0;

  const handOffToPopup = isEmbeddedInIframe();
  const callDisabled = !callable || (!handOffToPopup && twilio.state !== 'ready');

  const onCall = (): void => {
    if (callDisabled) return;
    if (selected) {
      const contactExternalId = selected.contactExternalId ?? undefined;
      placeOutbound(
        target,
        contactExternalId,
        personCallName(selected),
        contactExternalId ? selected.companyExternalId ?? undefined : undefined,
      );
      return;
    }
    placeOutbound(target);
  };

  const onDialFromSheet = (pick: IDialPick): void => {
    placeOutbound(pick.to, pick.contactExternalId, pick.contactName, pick.companyExternalId);
  };

  const choose = (person: IPerson): void => {
    setSelected(person);
    setNameQuery('');
    applyRawNumber('');
    setListOpen(false);
    setActiveKey(null);
  };

  const onFieldKey = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (people.length === 0) return false;
      e.preventDefault();
      setListOpen(true);
      setActiveKey(stepActive(people, activeKey, e.key));
      return true;
    }
    if (e.key === 'Escape') {
      if (!popoverOpen) return false;
      e.preventDefault();
      e.stopPropagation();
      setListOpen(false);
      setActiveKey(null);
      return true;
    }
    if (e.key === 'Tab') {
      setListOpen(false);
      return true;
    }
    if (e.key === 'Enter' && popoverOpen && active) {
      e.preventDefault();
      choose(active);
      return true;
    }
    return false;
  };

  const helper = ((): { text: string; tone: TFieldHelperTone } => {
    if (selected) {
      return selectedValid
        ? { text: `Calls ${displayPhone(selected.phone)}`, tone: 'info' }
        : { text: 'This contact\'s number is not a valid phone number, so it cannot be dialled.', tone: 'bad' };
    }
    if (nameQuery || !national) return { text: '', tone: 'info' };
    return numberHelper(national, country, manualParsed);
  })();

  const resultCount = popoverOpen ? people.length : 0;

  return (
    <div className={hideLabel ? `${classes.dialBar} ${classes.dialBarBare}` : classes.dialBar}>
      <div className={classes.dialRow}>
        <div className={classes.combo}>
          <CountryDialField
            inputId={fieldId}
            inputRef={inputRef}
            label="Call"
            inputLabel="Call a name or number"
            hideLabel={hideLabel}
            placeholder="Name or number"
            country={country}
            national={national}
            invalid={false}
            query={selected ? personShortTitle(selected) : nameQuery}
            onQueryChange={q => {
              setSelected(null);
              setNameQuery(q);
              setListOpen(true);
            }}
            onCountryChange={chooseCountry}
            onNationalChange={raw => {
              setSelected(null);
              applyRawNumber(raw);
              setListOpen(true);
            }}
            onSubmit={onCall}
            onKeyDown={onFieldKey}
            onFocus={() => {
              setListOpen(true);
              if (selected) inputRef.current?.select();
            }}
            onBlur={() => { setListOpen(false); }}
            helper={helper.text}
            helperTone={helper.tone}
            combobox={{
              listId,
              expanded: popoverOpen,
              activeId: active ? personOptionId(listId, active.key) : undefined,
            }}
            trailing={(
              <button
                type="button"
                className={`${classes.fieldButton} ${classes.fieldKeypad}`}
                aria-label="Open keypad"
                aria-haspopup="dialog"
                onClick={() => { setKeypadOpen(true); }}
              >
                <DialpadRoundedIcon aria-hidden style={{ fontSize: 20 }} />
              </button>
            )}
          />
          {popoverOpen ? (
            <div
              className={classes.popover}
              onMouseDown={e => { e.preventDefault(); }}
            >
              <div className={classes.popoverList}>
                <PeopleList
                  listId={listId}
                  label="Contacts and numbers"
                  groups={directory.groups}
                  query={query}
                  showCounts={query.length > 0}
                  activeKey={activeKey}
                  selectedKey={selected?.key ?? null}
                  surface="surface"
                  onPick={choose}
                  onActiveChange={setActiveKey}
                />
              </div>
              <div className={classes.popoverFoot}>
                <button
                  type="button"
                  className={classes.footAction}
                  onClick={() => {
                    setListOpen(false);
                    setKeypadOpen(true);
                  }}
                >
                  <DialpadRoundedIcon aria-hidden style={{ fontSize: 18 }} />
                  Open keypad
                </button>
                <span className={classes.footHints} aria-hidden>
                  <kbd className={classes.kbd}>↑</kbd>
                  <kbd className={classes.kbd}>↓</kbd>
                  <span>pick</span>
                  <kbd className={classes.kbd}>Enter</kbd>
                  <span>select</span>
                  <kbd className={classes.kbd}>Esc</kbd>
                  <span>close</span>
                </span>
              </div>
            </div>
          ) : null}
          <div className={classes.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {resultCount > 0 ? `${resultCount} ${resultCount === 1 ? 'result' : 'results'}` : ''}
          </div>
        </div>
        <button
          type="button"
          className={classes.callButton}
          disabled={callDisabled}
          onClick={onCall}
          aria-label="Place call"
          title={callable ? 'Call' : 'Pick someone or enter a valid number'}
        >
          <CallRoundedIcon aria-hidden style={{ fontSize: 22 }} />
        </button>
      </div>
      {dialFailed ? (
        <div className={classes.pickerState} role="alert">
          <span>That call could not be started, so nothing was dialled. Try again.</span>
        </div>
      ) : null}
      {busyWith ? (
        <div className={classes.pickerState} role="alert">
          <span>{popupBusyMessage(busyWith)}</span>
        </div>
      ) : null}
      {contactPhoneMissing ? (
        <div className={classes.pickerState}>
          <span>{CONTACT_NO_PHONE}</span>
        </div>
      ) : null}
      {hasTicket && !contactPhoneMissing && pickerPhase !== 'ready' ? (
        <div
          className={classes.pickerState}
          role={pickerPhase === 'error' || pickerPhase === 'partial' ? 'alert' : undefined}
        >
          {pickerPhase === 'loading' ? (
            <span>
              {contactScreen ? 'Loading this contact…' : 'Loading this ticket\'s contacts…'}
            </span>
          ) : null}
          {pickerPhase === 'error' ? (
            <>
              <span>
                {podDataFailed ? podDataFailedCopy : 'Could not load this company’s contacts.'}
              </span>
              <button type="button" className={classes.pickerStateAction} onClick={retryContacts}>
                Try again
              </button>
            </>
          ) : null}
          {pickerPhase === 'partial' ? (
            <>
              <span>
                {podDataFailed
                  ? podDataPartialCopy
                  : 'Some of this company’s contacts could not be loaded, so this list may be incomplete.'}
              </span>
              <button type="button" className={classes.pickerStateAction} onClick={retryContacts}>
                Try again
              </button>
            </>
          ) : null}
          {pickerPhase === 'empty' ? (
            <span>No one on this company has a phone number yet. Type a number to call.</span>
          ) : null}
        </div>
      ) : null}

      <AddPersonOverlay
        open={keypadOpen}
        mode="dial"
        crmSourceId={crmId}
        suggestCrmSourceId={config.psaId ?? null}
        companyId={podData.CompanyId}
        defaultCountry={defaultCountry}
        ticketPeople={ticketPeople}
        recentCalls={calls}
        onDial={onDialFromSheet}
        onClose={() => { setKeypadOpen(false); }}
      />
    </div>
  );
};

export default DialBar;
